import axios from 'axios';
import { fetchMaintenanceStatus } from 'Common.functions';
import { clone } from 'lodash';

const isRequestCanceled = (error) => {
  return axios.isCancel(error) || error?.code === 'ERR_CANCELED';
};

const constructUrl = (url) => {
  const { REACT_APP_BUILD_TYPE } = process.env;

  if (REACT_APP_BUILD_TYPE && REACT_APP_BUILD_TYPE === 'liveproduction') {
    return 'https://prodapi.rezrate.com/' + url;
  }

  if (REACT_APP_BUILD_TYPE && REACT_APP_BUILD_TYPE === 'livedevelopment') {
    return 'https://devapi.rezrate.com/' + url;
    // return 'http://localhost:8080/' + url;
  }
  // return 'http://localhost:8080/' + url;
  return `https://devapi.rezrate.com/` + url;
};

async function fetchData(url, params) {
  try {
    const result = await axios({
      url,
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      params,
    })
      .then((result) => result)
      .catch((error) => {
        throw error;
      });
    return result;
  } catch (err) {
    console.log('fetchData error: ', err);
    fetchMaintenanceStatus({
      updateLocalStorage: true,
    });
    throw err;
  }
}

async function fetchDataWithoutCredentials(url, params) {
  try {
    const result = await axios({
      url,
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      params,
    })
      .then((result) => result)
      .catch((error) => {
        throw error;
      });
    return result;
  } catch (err) {
    console.log('fetchDataWithoutCredentials error: ', err);
    fetchMaintenanceStatus({
      updateLocalStorage: true,
    });
    throw err;
  }
}

async function fetchDataAgnostic(url, params, headers, options = {}) {
  const completeHeaders = { 'Content-Type': 'application/json', ...headers };
  const { abortController, callback } = options;
  const { signal } = abortController || {};
  const axiosOptions = {
    url: constructUrl(url),
    method: 'GET',
    params,
    headers: completeHeaders,
  };

  if (options && abortController) {
    axiosOptions.signal = signal;
  }

  try {
    if (callback) {
      axios
        .get(constructUrl(url), axiosOptions)
        .then((result) => {
          callback(result);
        })
        .catch((err) => {
          callback(err);
        });
      return;
    } else {
      const result = await axios(axiosOptions)
        .then((result) => result)
        .catch((error) => {
          throw error;
        });
      return result;
    }
  } catch (err) {
    if (isRequestCanceled(err)) {
      throw err;
    }

    console.log('fetchDataAgnostic error: ', err);
    fetchMaintenanceStatus({
      updateLocalStorage: true,
    });
    throw err;
  }
}

async function fetchDataAgnosticWithoutCredentials(url, params, headers) {
  const completeHeaders = { 'Content-Type': 'application/json', ...headers };

  try {
    const result = await axios({
      url: constructUrl(url),
      method: 'GET',
      params,
      headers: completeHeaders,
    })
      .then((result) => result)
      .catch((error) => {
        throw error;
      });
    return result;
  } catch (err) {
    console.log('fetchDataAgnosticWithoutCredentials error: ', err);
    fetchMaintenanceStatus({
      updateLocalStorage: true,
    });
    throw err;
  }
}

async function postDataFM(url, params, dates) {
  try {
    const result = await axios({
      url,
      method: 'POST',
      params,
      header: { 'Access-Control-Allow-Origin': '*' },
    })
      .then((result) => result)
      .catch((error) => {
        throw error;
      });
    return result;
  } catch (err) {
    console.log('postDataFM error: ', err);
    fetchMaintenanceStatus({
      updateLocalStorage: true,
    });
    throw err;
  }
}

async function postData(url, params, dates) {
  try {
    const result = await axios({
      url,
      method: 'POST',
      params,
      header: { 'Access-Control-Allow-Origin': '*' },
      data: { dates },
    })
      .then((result) => result)
      .catch((error) => {
        throw error;
      });
    return result;
  } catch (err) {
    console.log('postData error: ', err);
    fetchMaintenanceStatus({
      updateLocalStorage: true,
    });
    throw err;
  }
}

async function postDataWithoutCredentials(url, params, data, headers) {
  try {
    const result = await axios({
      url: url,
      method: 'POST',
      headers: { ...headers },
      params,
      data: data ?? {},
    })
      .then((result) => result)
      .catch((error) => {
        throw error;
      });
    return result;
  } catch (err) {
    console.log('postDataWithoutCredentials error: ', err);
    fetchMaintenanceStatus({
      updateLocalStorage: true,
    });
    throw err;
  }
}

async function postDataAgnostic(url, params, data, headers) {
  try {
    const result = await axios({
      url: constructUrl(url),
      method: 'POST',
      headers: { ...headers },
      params,
      data: data ?? {},
    })
      .then((result) => result)
      .catch((error) => {
        throw error;
      });
    return result;
  } catch (err) {
    // Axios errors include request bodies; keep applicant data out of diagnostics.
    console.error('postDataAgnostic request failed', {
      status: err && err.response && Number.isFinite(err.response.status) ? err.response.status : undefined,
      reason: err && err.response ? 'http-error' : 'network-error',
    });
    fetchMaintenanceStatus({
      updateLocalStorage: true,
    });
    throw err;
  }
}

async function postDataAgnosticWithoutCredentials(url, params, data, headers) {
  try {
    const result = await axios({
      url: constructUrl(url),
      method: 'POST',
      headers: { ...headers },
      params,
      data: data ?? {},
    })
      .then((result) => result)
      .catch((error) => {
        throw error;
      });
    return result;
  } catch (err) {
    console.log('postDataAgnosticWithoutCredentials error: ', err);
    fetchMaintenanceStatus({
      updateLocalStorage: true,
    });
    throw err;
  }
}

async function fetchDataAuth0(url, params, headers) {
  const completeHeaders = { 'Content-Type': 'application/json', ...headers };

  try {
    const result = await axios({
      url: constructUrl(url),
      method: 'GET',
      params,
      headers: completeHeaders,
    })
      .then((result) => result)
      .catch((error) => {
        throw error;
      });
    return result;
  } catch (err) {
    console.log('fetchDataAuth0 error: ', err);
    fetchMaintenanceStatus({
      updateLocalStorage: true,
    });
    throw err;
  }
}

async function deleteData(url, params, data, headers) {
  const completeHeaders = { 'Content-Type': 'application/json', ...headers };

  try {
    const result = await axios({
      url: constructUrl(url),
      method: 'DELETE',
      params,
      data: data ?? {},
      headers: completeHeaders,
    })
      .then((result) => result)
      .catch((error) => {
        throw error;
      });
    return result;
  } catch (err) {
    console.log('deleteData error: ', err);
    fetchMaintenanceStatus({
      updateLocalStorage: true,
    });
    throw err;
  }
}

async function putData(url, params, data, headers, options = {}) {
  const completeHeaders = { 'Content-Type': 'application/json', ...headers };
  try {
    const result = await axios({
      url: constructUrl(url),
      method: 'PUT',
      params,
      data: data ?? {},
      headers: completeHeaders,
    })
      .then((result) => result)
      .catch((error) => {
        throw error;
      });
    return result;
  } catch (err) {
    // Axios errors can contain the submitted request body. Sensitive callers
    // use server-side safe diagnostics and suppress this legacy console copy.
    if (!options.suppressErrorLog && url !== 'candidate') {
      console.error('putData error: ', err);
    }
    fetchMaintenanceStatus({
      updateLocalStorage: true,
    });
    throw err;
  }
}

export {
  fetchData,
  putData,
  deleteData,
  postDataAgnosticWithoutCredentials,
  postData,
  fetchDataAuth0,
  fetchDataAgnostic,
  postDataFM,
  fetchDataWithoutCredentials,
  postDataWithoutCredentials,
  constructUrl,
  postDataAgnostic,
  fetchDataAgnosticWithoutCredentials,
};
